import {
  Box,
  styled,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useLoadDataList from 'src/hook/useLoadDataList';
import {
  getAssemblyCategories,
  updateAssemblyCategory,
  AssemblyCategory,
} from 'src/services/assembly-category';
import { handleApiError } from 'src/utils/api.utils';

type EditCategoryProps = {
  onSuccess: () => void;
};

export const EditCategory = ({ onSuccess }: EditCategoryProps) => {
  const { data: categories, onRefresh } = useLoadDataList(getAssemblyCategories);
  const [selectedCategory, setSelectedCategory] = useState<AssemblyCategory | null>(null);
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedCategory) {
      setName(selectedCategory.name);
    } else {
      setName('');
    }
  }, [selectedCategory]);

  const handleCategoryChange = (event: SelectChangeEvent<number>) => {
    const categoryId = event.target.value as number;
    const category = categories.find((c) => c.id === categoryId) || null;
    setSelectedCategory(category);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCategory) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }

    if (!name.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAssemblyCategory(selectedCategory.id, name.trim());
      toast.success('Catégorie modifiée avec succès');
      setSelectedCategory(null);
      setName('');
      onRefresh();
      onSuccess();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        Modifier une catégorie
      </Typography>

      <Form onSubmit={handleSubmit}>
        <FormControl fullWidth>
          <InputLabel id="category-select-label">Catégorie</InputLabel>
          <Select
            labelId="category-select-label"
            value={selectedCategory?.id ?? ''}
            label="Catégorie"
            onChange={handleCategoryChange}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCategory && (
          <>
            <TextField
              label="Nouveau nom"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />

            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Modification...' : 'Modifier'}
            </Button>
          </>
        )}
      </Form>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '20px',
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  maxWidth: '400px',
});
